import { useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, FileCheck2, Loader2, Upload } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { api, formatApiError } from "@/lib/api";

export default function UploadProofPage() {
  const { leadId } = useParams();
  const nav = useNavigate();
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const onPick = (f) => {
    if (!f) return;
    if (f.size > 10 * 1024 * 1024) {
      toast.error("File must be under 10MB");
      return;
    }
    const allowed = ["image/jpeg", "image/png", "image/webp", "application/pdf"];
    if (!allowed.includes(f.type)) {
      toast.error("Only JPEG, PNG, WEBP or PDF are allowed");
      return;
    }
    setFile(f);
  };

  const handleSubmit = async () => {
    if (!file) {
      toast.error("Please select your NEET scorecard file");
      return;
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/leads/${leadId}/scorecard`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Scorecard uploaded. Get ready for the test!");
      nav(`/quiz/${leadId}`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const skip = () => nav(`/quiz/${leadId}`);

  return (
    <div className="min-h-screen bg-white">
      <header className="border-b border-slate-200">
        <div className="mx-auto flex h-16 max-w-5xl items-center justify-between px-4 sm:px-6">
          <Link
            to="/register"
            className="flex items-center gap-2 text-sm text-slate-600 hover:text-[#0A2540]"
            data-testid="upload-back"
          >
            <ArrowLeft className="h-4 w-4" /> Back
          </Link>
          <div className="text-xs uppercase tracking-[0.22em] text-slate-500">
            Step 2 of 3 &middot; Verify
          </div>
        </div>
        <div className="h-1 w-2/3 bg-[#0A2540]" />
      </header>

      <main className="mx-auto max-w-2xl px-4 py-10 sm:px-6 lg:py-16">
        <div className="text-xs uppercase tracking-[0.22em] text-slate-500">
          NEET Verification
        </div>
        <h1 className="font-display mt-3 text-3xl font-bold tracking-tight text-[#0A2540] sm:text-4xl">
          Upload your NEET scorecard.
        </h1>
        <p className="mt-3 text-slate-600">
          A quick verification step that boosts your scholarship eligibility.
          Your file is stored securely and used only for admission verification.
        </p>

        <div
          onClick={() => fileRef.current?.click()}
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => {
            e.preventDefault();
            onPick(e.dataTransfer.files?.[0]);
          }}
          className="mt-8 cursor-pointer rounded-2xl border-2 border-dashed border-slate-300 bg-[#F8F9FA] p-10 text-center transition-colors hover:border-[#0A2540]"
          data-testid="upload-dropzone"
        >
          <input
            ref={fileRef}
            type="file"
            accept="image/jpeg,image/png,image/webp,application/pdf"
            className="hidden"
            onChange={(e) => onPick(e.target.files?.[0])}
            data-testid="upload-input"
          />
          {file ? (
            <div className="flex flex-col items-center gap-3">
              <FileCheck2 className="h-9 w-9 text-[#166534]" />
              <div className="font-medium text-[#0A2540]">{file.name}</div>
              <div className="text-xs text-slate-500">
                {(file.size / 1024).toFixed(1)} KB
              </div>
              <button
                type="button"
                className="text-xs text-slate-500 underline"
                onClick={(e) => {
                  e.stopPropagation();
                  setFile(null);
                }}
              >
                Choose a different file
              </button>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-3">
              <Upload className="h-9 w-9 text-[#0A2540]" strokeWidth={1.6} />
              <div className="font-medium text-[#0A2540]">
                Click or drop your scorecard here
              </div>
              <div className="text-xs text-slate-500">
                JPG, PNG, WEBP or PDF &middot; up to 10MB
              </div>
            </div>
          )}
        </div>

        <div className="mt-8 flex flex-col-reverse gap-3 sm:flex-row sm:justify-between">
          <button
            type="button"
            onClick={skip}
            className="text-sm text-slate-500 underline-offset-4 hover:underline"
            data-testid="upload-skip"
          >
            Skip for now &mdash; I&rsquo;ll upload later
          </button>
          <Button
            onClick={handleSubmit}
            disabled={submitting}
            className="h-12 bg-[#0A2540] px-6 text-sm font-semibold text-white hover:bg-[#1E3A5F]"
            data-testid="upload-submit"
          >
            {submitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Uploading…
              </>
            ) : (
              <>
                Continue to Test
                <ArrowRight className="ml-2 h-4 w-4" />
              </>
            )}
          </Button>
        </div>
      </main>
    </div>
  );
}
